import { Box, FormControl, FormHelperText } from '@mui/material';
import {
  StyledUnitSelect,
  StyledUnitMenuItem,
  unitSelectMenuProps,
  unitErrorTextStyles,
  unitErrorIconStyles,
} from 'src/components/StyledComponents/StyledUnitPicker';
import {
  FieldLabel,
  DropdownIcon,
} from 'src/components/StyledComponents/StyledPageComponents';
import { FC, useEffect, useState } from 'react';
import { errorMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { EnumDropdownProps } from 'src/models/FieldProps.interface';
import { getIn } from 'formik';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import {
  getUnmappedBasicProducts,
  getBasicProducts,
} from 'src/services/product.service';

interface ProductOption {
  id: string;
  name: string;
}

interface ProductSelectProps extends EnumDropdownProps {
  isUnmapped?: boolean;
  fontSize?: string;
  fontWeight?: string;
  borderColor?: string;
  placeholder?: string;
  additionalProducts?: ProductOption[];
  disabledProductIds?: string[];
}

const ProductSelect: FC<ProductSelectProps> = ({
  id,
  label,
  formikProps,
  sx,
  color = 'var(--primary-navy)',
  errorState = false,
  height = '40px',
  width = '450px',
  showLabel = true,
  isUnmapped = false,
  fontSize = '16px',
  fontWeight = '400',
  borderColor = 'var(--border)',
  placeholder,
  additionalProducts = [],
  disabledProductIds = [],
}) => {
  const { showSnackbar } = StyledSnackbar();

  const [fetchedProducts, setFetchedProducts] = useState<ProductOption[]>([]);

  const fetchProducts = async () => {
    try {
      const productData = isUnmapped
        ? await getUnmappedBasicProducts()
        : await getBasicProducts();

      setFetchedProducts(productData);
    } catch {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [isUnmapped]);

  // Merge additional products without triggering refetch
  const products = (() => {
    const mergedProducts = [...fetchedProducts];
    additionalProducts.forEach((additionalProduct) => {
      if (!mergedProducts.some((p) => p.id === additionalProduct.id)) {
        mergedProducts.push(additionalProduct);
      }
    });
    return mergedProducts;
  })();

  return (
    <Box mb={3}>
      {showLabel && <FieldLabel color={color}>{label}</FieldLabel>}
      <FormControl
        fullWidth
        error={
          getIn(formikProps.touched, id) &&
          (Boolean(getIn(formikProps.errors, id)) || errorState)
        }
        sx={{ m: 0 }}
      >
        <StyledUnitSelect
          id={id}
          name={id}
          value={getIn(formikProps.values, id)}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
          displayEmpty
          height={height}
          width={width}
          fontSize={fontSize}
          fontWeight={fontWeight}
          borderColor={borderColor}
          hasError={
            (getIn(formikProps.touched, id) &&
              Boolean(getIn(formikProps.errors, id))) ||
            errorState
          }
          IconComponent={ExpandMoreOutlined}
          sx={sx}
          renderValue={(selected) => {
            const selectedProduct = products.find(
              (product) => product.id === selected
            );
            return selectedProduct
              ? selectedProduct.name
              : placeholder || `Select ${label}`;
          }}
          MenuProps={unitSelectMenuProps}
        >
          {products.map((product) => (
            <StyledUnitMenuItem
              key={product.id}
              value={product.id}
              disabled={disabledProductIds.includes(product.id)}
            >
              {product.name}
            </StyledUnitMenuItem>
          ))}
        </StyledUnitSelect>
        {getIn(formikProps.touched, id) && getIn(formikProps.errors, id) && (
          <FormHelperText sx={{ ...unitErrorTextStyles }}>
            <Box
              component="i"
              className="bx bxs-error-circle"
              sx={unitErrorIconStyles}
            />
            {getIn(formikProps.errors, id)}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default ProductSelect;
